import React from 'react';
import type { LucideIcon } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

export interface StatCardProps {
  label: string;
  value: number | string;
  icon: LucideIcon;
  hint?: string;
  loading?: boolean;
  className?: string;
}

const StatCard: React.FC<StatCardProps> = ({
  label,
  value,
  icon: Icon,
  hint,
  loading = false,
  className,
}) => (
  <Card
    className={cn(
      'flex flex-col gap-2.5 p-4 transition-[box-shadow,border-color,translate] duration-200 ease-shell',
      // Only the ambient layer grows on hover; the contact shadow stays pinned,
      // which is what reads as the card lifting rather than the shadow swelling.
      'shadow-[0_1px_2px_rgb(0_0_0/0.04)] hover:-translate-y-px hover:border-primary/25',
      'hover:shadow-[0_1px_2px_rgb(0_0_0/0.04),0_10px_28px_rgb(0_0_0/0.08)]',
      'motion-reduce:transition-none motion-reduce:hover:translate-y-0',
      className,
    )}
  >
    <div className="flex items-start justify-between gap-3">
      <p className="revy-cjk min-w-0 text-sm font-medium leading-[1.5] text-muted-foreground">
        {label}
      </p>
      <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
        <Icon className="size-[18px]" strokeWidth={1.9} />
      </span>
    </div>

    <div className="flex flex-col gap-1">
      {loading ? (
        <Skeleton className="h-9 w-20" />
      ) : (
        <span className="revy-figure text-3xl font-semibold leading-none tracking-tight">
          {value}
        </span>
      )}
      {hint && <p className="text-xs leading-[1.5] text-muted-foreground">{hint}</p>}
    </div>
  </Card>
);

export default StatCard;
